import type { User } from '@braze/web-sdk'
import type { ProductVariant } from '@getstep/proto/common'
import { notNullish } from '@getstep/sdk/dist/util/Assert'

import { AnalyticsEvent } from '@lib/analytics/events'
import * as mixpanel from '@lib/analytics/mixpanel'
import { hasCookieConsent } from '@lib/hooks/useCookieConsent'

export interface Braze {
    logCustomEvent: (eventName: string, eventProperties?: object) => boolean
    changeUser: (userId: string, signature?: string) => void
    getUser: () => User | undefined
}

// Mapping of events that have different names in Segment
const SEGMENT_EVENT_MAP: Record<string, AnalyticsEvent> = {
    [AnalyticsEvent.SIGNUP]: AnalyticsEvent.SIGNUP_SEGMENT,
    [AnalyticsEvent.EARLYPAY_BANK_CONNECTION]:
        AnalyticsEvent.LINKED_ACCOUNT_ADDED,
}

// Whitelist of events that should be sent to Segment
const SEGMENT_ALLOWED_EVENTS = new Set<string>([
    AnalyticsEvent.SIGNUP_SEGMENT,
    AnalyticsEvent.KYC_ATTEMPT,
    AnalyticsEvent.TRIAL_STARTED,
    AnalyticsEvent.LINKED_ACCOUNT_ADDED,
    AnalyticsEvent.EARLYPAY_APPLICATION_SUBMITTED,
])

export interface UserAttributes {
    email?: string
    phone?: string
    firstName?: string
    lastName?: string
    dateOfBirth?: string // For Braze and internal use
    birthday?: string // Segment reserved trait (ISO-8601 date format: YYYY-MM-DD)
    productVariant?: ProductVariant
    platform?: string
    userAgent?: string
    ipAddress?: string
    // Meta Pixel traits for Facebook CAPI
    fbc?: string | null
    fbp?: string | null
    fbclid?: string | null
    // UTM parameters
    utm_source?: string
    utm_campaign?: string
    utm_ad?: string
    utm_medium?: string
    utm_id?: string
    utm_content?: string
    utm_term?: string
    // Google Ads parameters
    gclid?: string
    adset?: string
    network?: string
    matchtype?: string
    device?: string
}

function splitDateWithRegex(dateString: string) {
    // Try ISO format first (YYYY-MM-DD)
    const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (isoMatch) {
        const [, year, month, day] = isoMatch
        return {
            day: parseInt(day, 10),
            month: parseInt(month, 10),
            year: parseInt(year, 10),
        }
    }

    // Try DD/MM/YYYY format (with optional spaces around slashes)
    const dmyMatch = dateString.match(/^(\d{2})\s?\/\s?(\d{2})\s?\/\s?(\d{4})$/)
    if (dmyMatch) {
        const [, day, month, year] = dmyMatch
        return {
            day: parseInt(day, 10),
            month: parseInt(month, 10),
            year: parseInt(year, 10),
        }
    }

    throw new Error(
        `Invalid date format. Expected "YYYY-MM-DD", "DD/MM/YYYY", or "DD / MM / YYYY" but received "${dateString}"`,
    )
}

class AnalyticsService {
    initialized = false
    braze: Braze | undefined
    private attributionData: Record<string, any> = {}
    private pendingIdentity?: { userId: string; attributes: UserAttributes }
    private pendingEvents: Array<{
        type: 'track' | 'pageView'
        name: string
        properties: Record<string, any>
    }> = []
    private currentUserTraits: { email?: string; phone?: string } = {}
    private browserContext: {
        userAgent?: string
        ipAddress?: string
    } = {}
    private ipFetchPromise?: Promise<string>

    // SessionStorage keys
    private readonly PENDING_EVENTS_KEY = 'step_analytics_pending_events'
    private readonly PENDING_IDENTITY_KEY = 'step_analytics_pending_identity'
    private readonly ATTRIBUTION_DATA_KEY = 'step_analytics_attribution_data'

    async initialize(searchParams?: URLSearchParams) {
        if (this.initialized || typeof window === 'undefined') {
            return
        }
        try {
            // Load any pending data from previous session
            this.loadPendingDataFromStorage()

            // Dynamically import Braze SDK only on client side
            const brazeModule = await import('@braze/web-sdk')
            this.braze = brazeModule

            brazeModule.initialize(
                process.env.NEXT_PUBLIC_BRAZE_API_KEY ?? '',
                {
                    baseUrl: process.env.NEXT_PUBLIC_BRAZE_BASE_URL ?? '',
                    enableLogging: false,
                    sessionTimeoutInSeconds: 30,
                    minimumIntervalBetweenTriggerActionsInSeconds: 5,
                    openCardsInNewTab: true,
                    allowUserSuppliedJavascript: false,
                    localization: 'en',
                },
            )

            // Open session
            brazeModule.openSession()

            mixpanel.init(searchParams)
            this.initialized = true
        } catch (error) {
            console.error('Failed to initialize Analytics:', error)
        }
    }

    // Page tracking
    async trackPageView(
        properties: Record<
            string,
            string | boolean | number | Record<string, string>
        > = {},
    ) {
        if (!this.initialized) {
            await this.initialize()
        }

        const eventName = AnalyticsEvent.PAGE_VIEWED
        const context = await this.getBrowserContext()

        // Merge properties with attribution data and browser context
        const enrichedProperties = {
            ...this.attributionData,
            ...properties,
            userAgent: context.userAgent,
            ipAddress: context.ipAddress,
        }

        // Check cookie consent - if not granted, queue for later
        if (!hasCookieConsent()) {
            this.pendingEvents.push({
                type: 'pageView',
                name: eventName,
                properties: enrichedProperties,
            })
            this.savePendingEventsToStorage()
            return
        }

        mixpanel.track(eventName, enrichedProperties)
        this.braze?.logCustomEvent(eventName, enrichedProperties)
        window.AF?.('pba', 'event', {
            ...enrichedProperties,
            eventType: 'EVENT',
            eventName,
        })
    }

    // Custom event tracking
    async trackEvent(
        eventName: AnalyticsEvent | string,
        properties: Record<
            string,
            | string
            | boolean
            | number
            | Record<string, string>
            | undefined
            | null
        > = {},
    ) {
        if (!this.initialized) {
            await this.initialize()
        }

        const context = await this.getBrowserContext()

        // Merge properties with attribution data and browser context
        const enrichedProperties = {
            ...this.attributionData,
            ...properties,
            userAgent: context.userAgent,
            ipAddress: context.ipAddress,
        }

        // Map event name to Segment-specific name if needed
        const segmentEventName = SEGMENT_EVENT_MAP[eventName] ?? eventName

        // Check cookie consent - if not granted, queue for later
        if (!hasCookieConsent()) {
            this.pendingEvents.push({
                type: 'track',
                name: eventName,
                properties: enrichedProperties,
            })
            this.savePendingEventsToStorage()
            return
        }

        mixpanel.track(eventName, enrichedProperties)
        this.braze?.logCustomEvent(eventName, enrichedProperties)

        // Only send whitelisted events to Segment
        if (SEGMENT_ALLOWED_EVENTS.has(segmentEventName)) {
            window.analytics?.track(
                segmentEventName,
                enrichedProperties,
                this.getSegmentContext(),
            )
        }

        window.AF?.('pba', 'event', {
            ...enrichedProperties,
            eventType: 'EVENT',
            eventName,
        })
    }

    // User identification
    async identifyUser(userId: string, userAttributes: UserAttributes = {}) {
        if (!this.initialized) {
            await this.initialize()
        }

        const context = await this.getBrowserContext()

        // Merge attribution data with user attributes and browser context
        const allAttributes = {
            ...this.attributionData,
            ...userAttributes,
            userAgent: context.userAgent,
            ipAddress: context.ipAddress,
        }

        // Check cookie consent - if not granted, queue for later
        if (!hasCookieConsent()) {
            this.pendingIdentity = { userId, attributes: allAttributes }
            this.savePendingIdentityToStorage()
            return
        }

        // Store previous anonymous ID for reference if needed
        const previousAnonymousId = sessionStorage.getItem('step_session_id')
        if (previousAnonymousId) {
            // Add anonymous ID to attribution data for cross-reference
            this.attributionData.previous_anonymous_id = `anonymous_${previousAnonymousId}`
        }

        this.braze?.changeUser(userId)
        mixpanel.identifyUser(userId)
        window.AF?.('pba', 'setCustomerUserId', userId)
        window.analytics?.identify(userId, allAttributes)
        if (Object.keys(allAttributes).length > 0) {
            await this.setUserAttributes(allAttributes)
        }
    }

    // Set user attributes
    async setUserAttributes(attributes: UserAttributes) {
        if (!this.initialized) {
            await this.initialize()
        }

        // Store email and phone for context.traits in track events
        if (attributes.email) {
            this.currentUserTraits.email = attributes.email
        }
        if (attributes.phone) {
            this.currentUserTraits.phone = attributes.phone
        }

        const user = this.braze?.getUser()
        if (notNullish(user)) {
            // Standard attributes
            if (attributes.email) user.setEmail(attributes.email)
            if (attributes.firstName) user.setFirstName(attributes.firstName)
            if (attributes.lastName) user.setLastName(attributes.lastName)
            if (attributes.phone) user.setPhoneNumber(attributes.phone)
            try {
                if (attributes.dateOfBirth) {
                    const { day, month, year } = splitDateWithRegex(
                        attributes.dateOfBirth,
                    )
                    user.setDateOfBirth(year, month, day)
                }
            } catch (error) {
                console.warn(error)
            }

            // Custom attributes
            Object.keys(attributes).forEach((key) => {
                if (
                    ![
                        'email',
                        'firstName',
                        'lastName',
                        'phone',
                        'dateOfBirth',
                    ].includes(key)
                ) {
                    user.setCustomUserAttribute(key, (attributes as any)[key])
                }
            })
        }
        window.analytics?.identify(attributes)
        mixpanel.updateAttributes(attributes)
    }

    /**
     * Set attribution data to be included in all future events
     *
     * This method allows ClickTracker or other components to provide attribution
     * context that will be automatically included in all tracking events.
     *
     * @param attribution Attribution data from marketing campaigns, referrals, etc.
     */
    setAttributionData(attribution: Record<string, any>) {
        this.attributionData = { ...attribution }
        this.saveAttributionDataToStorage()
    }

    /**
     * Get current attribution data
     */
    getAttributionData(): Record<string, any> {
        return { ...this.attributionData }
    }

    /**
     * Process pending events when consent is granted
     * This should be called when the user accepts cookie consent
     */
    async processPendingEvents() {
        if (!hasCookieConsent()) {
            return
        }

        // Process pending identity first
        if (this.pendingIdentity) {
            const { userId, attributes } = this.pendingIdentity
            this.pendingIdentity = undefined

            // Store previous anonymous ID for reference if needed
            const previousAnonymousId =
                sessionStorage.getItem('step_session_id')
            if (previousAnonymousId) {
                this.attributionData.previous_anonymous_id = `anonymous_${previousAnonymousId}`
            }

            this.braze?.changeUser(userId)
            mixpanel.identifyUser(userId)
            window.AF?.('pba', 'setCustomerUserId', userId)
            if (Object.keys(attributes).length > 0) {
                await this.setUserAttributes(attributes)
            }
        }

        // Process pending events sequentially
        const eventsToProcess = [...this.pendingEvents]
        this.pendingEvents = []

        for (const event of eventsToProcess) {
            await this.sendEvent(event.name, event.properties)
        }

        // Clear pending data from sessionStorage after processing
        this.clearPendingDataFromStorage()
    }

    /**
     * Manually start session recording
     */
    async startSessionRecording() {
        if (!this.initialized) {
            await this.initialize()
        }

        // Check cookie consent before starting session recording
        if (!hasCookieConsent()) {
            return
        }

        mixpanel.startSessionRecording()
    }

    /**
     * Stop session recording
     */
    async stopSessionRecording() {
        if (!this.initialized) {
            await this.initialize()
        }
        mixpanel.stopSessionRecording()
    }

    /**
     * Build Segment context with email and phone traits for Facebook matching
     */
    private getSegmentContext():
        | { context: { traits: { email?: string; phone?: string } } }
        | undefined {
        if (!this.currentUserTraits.email && !this.currentUserTraits.phone) {
            return undefined
        }

        const traits: { email?: string; phone?: string } = {}
        if (this.currentUserTraits.email) {
            traits.email = this.currentUserTraits.email
        }
        if (this.currentUserTraits.phone) {
            traits.phone = this.currentUserTraits.phone
        }

        return { context: { traits } }
    }

    /**
     * Send a single event to all analytics providers
     */
    private async sendEvent(
        eventName: string,
        properties: Record<string, any>,
    ): Promise<void> {
        return new Promise((resolve) => {
            try {
                // Map event name to Segment-specific name if needed
                const segmentEventName =
                    SEGMENT_EVENT_MAP[eventName] ?? eventName

                mixpanel.track(eventName, properties)
                this.braze?.logCustomEvent(eventName, properties)

                // Only send whitelisted events to Segment
                if (SEGMENT_ALLOWED_EVENTS.has(segmentEventName)) {
                    window.analytics?.track(
                        segmentEventName,
                        properties,
                        this.getSegmentContext(),
                    )
                }

                window.AF?.('pba', 'event', {
                    ...properties,
                    eventType: 'EVENT',
                    eventName,
                })
            } catch (error) {
                console.warn('Failed to send event:', eventName, error)
            }
            // Small delay to prevent overwhelming analytics services
            setTimeout(resolve, 50)
        })
    }

    private savePendingEventsToStorage() {
        if (typeof window === 'undefined') return

        try {
            sessionStorage.setItem(
                this.PENDING_EVENTS_KEY,
                JSON.stringify(this.pendingEvents),
            )
        } catch (error) {
            console.warn(
                'Failed to save pending events to sessionStorage:',
                error,
            )
        }
    }

    private savePendingIdentityToStorage() {
        if (typeof window === 'undefined') return

        try {
            if (this.pendingIdentity) {
                sessionStorage.setItem(
                    this.PENDING_IDENTITY_KEY,
                    JSON.stringify(this.pendingIdentity),
                )
            } else {
                sessionStorage.removeItem(this.PENDING_IDENTITY_KEY)
            }
        } catch (error) {
            console.warn(
                'Failed to save pending identity to sessionStorage:',
                error,
            )
        }
    }

    private saveAttributionDataToStorage() {
        if (typeof window === 'undefined') return

        try {
            if (Object.keys(this.attributionData).length > 0) {
                sessionStorage.setItem(
                    this.ATTRIBUTION_DATA_KEY,
                    JSON.stringify(this.attributionData),
                )
            } else {
                sessionStorage.removeItem(this.ATTRIBUTION_DATA_KEY)
            }
        } catch (error) {
            console.warn(
                'Failed to save attribution data to sessionStorage:',
                error,
            )
        }
    }

    private loadPendingDataFromStorage() {
        if (typeof window === 'undefined') return

        try {
            // Load pending events
            const eventsData = sessionStorage.getItem(this.PENDING_EVENTS_KEY)
            if (eventsData) {
                this.pendingEvents = JSON.parse(eventsData)
            }

            // Load pending identity
            const identityData = sessionStorage.getItem(
                this.PENDING_IDENTITY_KEY,
            )
            if (identityData) {
                this.pendingIdentity = JSON.parse(identityData)
            }

            // Load attribution data
            const attributionData = sessionStorage.getItem(
                this.ATTRIBUTION_DATA_KEY,
            )
            if (attributionData) {
                this.attributionData = JSON.parse(attributionData)
            }
        } catch (error) {
            console.warn(
                'Failed to load pending data from sessionStorage:',
                error,
            )
        }
    }

    private clearPendingDataFromStorage() {
        if (typeof window === 'undefined') return

        try {
            sessionStorage.removeItem(this.PENDING_EVENTS_KEY)
            sessionStorage.removeItem(this.PENDING_IDENTITY_KEY)
        } catch (error) {
            console.warn(
                'Failed to clear pending data from sessionStorage:',
                error,
            )
        }
    }

    /**
     * Get browser context including user agent and IP address
     */
    private async getBrowserContext(): Promise<{
        userAgent: string
        ipAddress: string
    }> {
        return {
            userAgent: this.getUserAgent(),
            ipAddress: await this.getClientIp(),
        }
    }

    /**
     * Get the current user agent
     */
    private getUserAgent(): string {
        if (!this.browserContext.userAgent) {
            this.browserContext.userAgent =
                typeof window !== 'undefined' ? navigator.userAgent : ''
        }
        return this.browserContext.userAgent
    }

    /**
     * Fetch client IP address from API route (cached after first fetch)
     */
    private async getClientIp(): Promise<string> {
        // Return cached IP if available
        if (this.browserContext.ipAddress) {
            return this.browserContext.ipAddress
        }

        // If already fetching, wait for that promise
        if (this.ipFetchPromise) {
            return this.ipFetchPromise
        }

        // Start new fetch
        this.ipFetchPromise = (async () => {
            try {
                const response = await fetch('/api/get-client-ip')
                if (!response.ok) {
                    throw new Error('Failed to fetch IP')
                }
                const data = await response.json()
                this.browserContext.ipAddress = data.ip
                return data.ip
            } catch (error) {
                console.warn('Failed to fetch client IP:', error)
                // Cache empty string to avoid repeated failed requests
                this.browserContext.ipAddress = ''
                return ''
            } finally {
                // Clear the promise so subsequent calls can use cached value
                this.ipFetchPromise = undefined
            }
        })()

        return this.ipFetchPromise
    }
}

// Create singleton instance
const analyticsService = new AnalyticsService()

export default analyticsService
